import PriceDisplay from '@/components/PriceDisplay';
import { payableAmountSelector } from '@/features/purchase/purchaseSelectors';
import useLocalizedCurrencyFormatter from '@/hooks/useLocalizedCurrencyFormatter';
import { useStartedExchangeOperation } from '@/hooks/useStartedExchangeOperation';
import { TransText } from '@/i18n/trans/text';
import { useSelector } from '@/store/utils';
import { Divider, Icons, Typography } from '@fleet/webshop-ui';
import type { FC } from 'react';

// "Reusable sum" breakdown row. Sits between the summary body (Additional
// fees etc.) and the final Change total row, separated from the latter by
// the host's primary divider. Sized to match the other small summary rows
// (body2-bold), not the final total.
//
// Note: the "Booking total" (`confirmedPrice`) row was intentionally
// dropped — the value is accurate but too nuanced for a public-facing app
// (it needs back-office domain knowledge to interpret).
export const ChangeBookingSummaryRows: FC = () => {
  // Credit applied from the original payment, off the exchange operation
  // started this session. `null` (no operation yet / BE didn't populate
  // it) → '-'.
  const reusableAmount = useStartedExchangeOperation()?.reusableAmount ?? null;
  return (
    <>
      <Divider variant="strong" />
      <div className="flex flex-col gap-2 p-4">
        <div className="flex justify-between">
          <div className="flex gap-2">
            <Icons.ticketChange className="text-primary" />
            <Typography variant="body2-bold">
              <TransText i18nKey="reusableSum" />
            </Typography>
          </div>
          <Typography variant="body2-bold" className="text-primary">
            {reusableAmount === null ? (
              '-'
            ) : (
              <PriceDisplay amount={reusableAmount} />
            )}
          </Typography>
        </div>
      </div>
    </>
  );
};

// The change-booking checkout's "Change total" = the BE-provided amount
// still to be paid (`payableAmount`), formatted as an absolute currency.
// This is the post-credit figure (reused payment, change fees, vouchers
// already netted out), not the raw `exchangePrice` delta used on the
// offer-page preview footer. Renders '-' until the value is available.
export const useChangeTotal = (): string => {
  const payableAmount = useSelector(payableAmountSelector);
  const numberFormatter = useLocalizedCurrencyFormatter();
  return payableAmount === null ? '-' : numberFormatter.format(payableAmount);
};
